import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { colors, fontSize } from "style";
import Tooltip from "components/Tooltip";

/**
 * Shows a radio button group
 *
 * @param {String}   name
 * @param {Any}      value
 * @param {Array}    options
 * @param {String}   position
 * @param {String}   direction
 * @param {String}   variant
 * @param {Function} onChange
 */

const Radio = ({ name, value, options, direction, size, variant, disabled, required, onChange }) => {
  const isButton = variant === "button";
  const handleChange = (e) => {
    if (onChange) {
      onChange(name, e.target.value);
    }
  };

  const option = (item, index) => (
    <div key={`radio-${name}-${index}`} css={styles.item_container}>
      <StyledRadio
        key={index}
        id={`radio-${name}-${index}`}
        value={item.value}
        name={name}
        onChange={handleChange}
        type="radio"
        checked={item.value === value}
        size={size}
        disabled={disabled}
        required={required}
        css={isButton && styles.button_input}
      />
      <label
        htmlFor={`radio-${name}-${index}`}
        css={[styles.label(size || item.size), isButton && styles.button_label]}
      >
        {item.label || item.value}
      </label>
    </div>
  );

  return (
    <div css={[styles.container(direction, size), isButton && styles.button_container]}>
      {options.map((item, index) =>
        item.description ? (
          <Tooltip key={`radio-${name}-${index}`} tooltipId={item.description} hideIcon={true}>
            {option(item, index)}
          </Tooltip>
        ) : (
          option(item, index)
        )
      )}
    </div>
  );
};

/**
 * CheckBox
 */
const StyledRadio = styled.input`
  width: ${({ size }) => (size === RADIO_SIZE.small ? `1.5rem` : `2rem`)};
  height: ${({ size }) => (size === RADIO_SIZE.small ? `1.5rem` : `2rem`)};
  border-radius: 100%;
  border: solid 1px ${colors.grayAnatomyLight3};
  background: #fff;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  vertical-align: top;
  appearance: none;
  color-adjust: exact;

  &:hover:not(:disabled) {
    border-color: ${colors.purpleRainBase};
  }

  &:checked {
    ${(props) =>
      props.inTable &&
      `
      background: ${colors.purpleRainDark1};
      border-color: ${colors.purpleRainDark1};
    `}

    &:after {
      content: " ";
      width: 0.8rem;
      height: 0.8rem;
      border-radius: 100%;
      background: ${(props) => (props.inTable ? `#fff` : colors.purpleRainBase)};
    }
  }
`;

const styles = {
  button_container: css`
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
  `,
  button_input: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;

    &:checked + label {
      color: ${colors.purpleRainBase};
      background: ${colors.purpleRainLight3};
      border-color: ${colors.purpleRainBase};
    }

    &:focus-visible + label {
      outline: 2px solid ${colors.purpleRainBase};
      outline-offset: 2px;
    }

    &:disabled + label {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
  button_label: css`
    margin: 0;
    padding: 1rem 1.6rem;
    border: 1px solid ${colors.grayAnatomyLight3};
    border-radius: 0.6rem;
    background: #fff;
    color: ${colors.secondary};
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;

    input:not(:disabled) + &:hover {
      border-color: ${colors.purpleRainBase};
    }
  `,
  container: (direction, size) => css`
    order: 1;

    ${direction === RADIO_DIRECTION.horizontal &&
    `
      display: flex;
      gap: ${size === RADIO_SIZE.small ? `1rem` : `2rem`};
    `}
  `,
  item_container: css`
    display: flex;
    align-items: center;
  `,
  label: (size) => css`
    margin-left: ${size === RADIO_SIZE.small ? `0.5rem` : `1rem`};
    font-size: ${fontSize.small};
    font-size: ${size === RADIO_SIZE.small && fontSize.xxsmall};
    font-size: ${size === RADIO_SIZE.xlarge && fontSize.xlarge};
    font-weight: 400;
  `,
};

const RADIO_DIRECTION = {
  horizontal: "horizontal",
  vertical: "vertical",
};

const RADIO_SIZE = {
  small: "small",
  large: "large",
  xlarge: "xlarge",
};

Radio.propTypes = {
  name: PropTypes.string,
  value: PropTypes.any,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  direction: PropTypes.string,
  size: PropTypes.string,
  variant: PropTypes.oneOf(["default", "button"]),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

export { RADIO_DIRECTION, RADIO_SIZE };

export default Radio;
